import { type App, type DefineComponent, type Plugin } from 'vue'
import { RouteRecordRaw } from 'vue-router'
import { route, prefixRoutes } from '@router/utils'
import type { Options } from '@/router'

import { 
  createRoutes as createProfileRoutes, BASE_MODEL_NAME as PROFILE_ROOT_PATH,
} from './modules/Profile/router'
import { 
  createRoutes as createNotificationsRoutes, BASE_MODEL_NAME as NOTIFICATIONS_ROOT_PATH,
  NOTIFICATIONS_BASE,
} from './modules/Notifications/router'
import {
  createRoutes as createStaffRoutes, BASE_MODEL_NAME as STAFF_ROOT_PATH,
} from './modules/Staff/router'
import {
  createRoutes as createCarsRoutes, BASE_MODEL_NAME as CARS_ROOT_PATH,
} from './modules/Cars/router'
import {
  createRoutes as createPaymentsRoutes, BASE_MODEL_NAME as PAYMENTS_ROOT_PATH,
} from './modules/Payments/router'
import {
  createRoutes as createOrdersRoutes, BASE_MODEL_NAME as ORDERS_ROOT_PATH,
} from './modules/Orders/router'
import {
  createRoutes as createPagesRoutes, BASE_MODEL_NAME as SIMPLE_PAGES_ROOT_PATH,
} from './modules/SimplePages/router'
import {
  createRoutes as createClientsRoutes, BASE_MODEL_NAME as CLIENTS_ROOT_PATH,
} from './modules/Clients/router'

// import { createRoutes as createTransactionsRoutes } from './modules/Transactions/router'
import { i18n } from '@/i18n'

import { PROFILE_VIEW_NAME } from './routes'

const CabinetView = () => import('./views/CabinetView.vue')
const CabinetInstaller = () => import('./components')
const Route403 = () => import('./views/Route403.vue')
const Route404 = () => import('./views/Route404.vue')
const Route500 = () => import('./views/Route500.vue')

function installer<T extends DefineComponent>(
  View: any,
  Installer: () => Promise<T>,
  app: App<Element>
): () => Promise<Awaited<T>> {
  return function (): Promise<Awaited<T>> {
    return Promise.all([View(), Installer()]).then(function (result) {
      const [ViewComponent, installer] = result
      app.use(installer?.default || installer?.install || installer)
      return ViewComponent
    })
  }
}

const createCabinetRoutes = (options: Options): RouteRecordRaw[] => {
  return [
    prefixRoutes(
      PROFILE_ROOT_PATH, //profile
      createProfileRoutes(options)
    ),
    prefixRoutes(
      NOTIFICATIONS_ROOT_PATH,
      createNotificationsRoutes(options),
      { name: NOTIFICATIONS_BASE }
    ),
    prefixRoutes(
      STAFF_ROOT_PATH, //staff
      createStaffRoutes(options),
    ),
    prefixRoutes(
      CLIENTS_ROOT_PATH, //clients
      createClientsRoutes(options),
    ),
    prefixRoutes(
      CARS_ROOT_PATH, //cars
      createCarsRoutes(options),
    ),
    prefixRoutes(
      PAYMENTS_ROOT_PATH, //payments
      createPaymentsRoutes(options),
    ),
    prefixRoutes(
      ORDERS_ROOT_PATH, //orders
      createOrdersRoutes(options),
    ),
    prefixRoutes(
      SIMPLE_PAGES_ROOT_PATH, //pages(info)
      createPagesRoutes(options),
    ),
  ]
}

export function createRoutes(options: Options): RouteRecordRaw[] {
  const { app } = options
  const { t: $t } = i18n.global
  return [
    route(
      '',
      installer<any>(
        CabinetView,
        CabinetInstaller as unknown as () => Promise<Plugin>,
        app
      ),
      'cabinet',
      {
        children: [
          ...createCabinetRoutes(options),
          {
            path: '/:pathMatch(.*)*',
            name: 'cabinet:not-found',
            component: Route404,
            meta: {
              title: () => $t(''),
            },
          },
          {
            path: '/:pathMatch(.*)*',
            name: 'cabinet:no-access',
            component: Route403,
            meta: {
              title: () => $t('403'),
            },
          },
          {
            path: '/:pathMatch(.*)*',
            name: 'cabinet:no-server-error',
            component: Route500,
            meta: {
              title: () => $t('500'),
            },
          },
        ],
        redirect: { name: PROFILE_VIEW_NAME},
      }
    ),
  ]
}
